import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiRequestError, api } from '@/lib/api';

export interface DiscussionThreadRecord {
  id: string;
  tenant_id: string;
  thread_type: 'entity' | 'channel' | 'dm';
  entity_type: string | null;
  entity_id: string | null;
  name: string | null;
  description: string | null;
  is_private: boolean;
  security_level: number;
  external_scope: boolean;
  restricted_to: string[];
  created_by: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  current_user_role?: string | null;
  current_user_last_read_at?: string | null;
  created?: boolean;
}

export interface DiscussionMessageRecord {
  id: string;
  thread_id: string;
  tenant_id: string;
  sender_user_id: string | null;
  sender_external_type: string | null;
  sender_external_id: string | null;
  sender_name: string;
  content: string;
  content_type: string;
  voice_url: string | null;
  voice_duration_s: number | null;
  voice_transcript: string | null;
  attachments: unknown[];
  security_level: number;
  is_internal_note: boolean;
  parent_id: string | null;
  reactions: Record<string, unknown>;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
}

interface DiscussionMessagesResponse {
  items: DiscussionMessageRecord[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  thread: DiscussionThreadRecord;
}

function buildEntityThreadPath(entityType: string, entityId: string): string {
  return `/chat-threads/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`;
}

export function useEntityDiscussionThread(entityType: string | undefined, entityId: string | undefined, enabled: boolean) {
  const [thread, setThread] = useState<DiscussionThreadRecord | null>(null);
  const [messages, setMessages] = useState<DiscussionMessageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canLoad = useMemo(() => Boolean(enabled && entityType && entityId), [enabled, entityType, entityId]);

  const loadMessages = useCallback(async (threadId: string) => {
    const response = await api.get<DiscussionMessagesResponse>(`/chat-threads/${threadId}/messages?page=1&limit=100`);
    setThread(response.thread);
    setMessages(response.items ?? []);
    try {
      await api.post(`/chat-threads/${threadId}/read`, {});
    } catch {
      // Ignore read receipt failures so the thread remains usable.
    }
  }, []);

  const ensureThread = useCallback(async () => {
    if (!entityType || !entityId) return null;

    try {
      return await api.get<DiscussionThreadRecord>(buildEntityThreadPath(entityType, entityId));
    } catch (error) {
      if (!(error instanceof ApiRequestError) || error.status !== 404) {
        throw error;
      }
      return api.post<DiscussionThreadRecord>(buildEntityThreadPath(entityType, entityId), {});
    }
  }, [entityId, entityType]);

  const refresh = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    setError(null);
    try {
      const ensuredThread = await ensureThread();
      if (!ensuredThread) return;
      setThread(ensuredThread);
      await loadMessages(ensuredThread.id);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load discussion');
    } finally {
      setLoading(false);
    }
  }, [canLoad, ensureThread, loadMessages]);

  const sendMessage = useCallback(async (content: string) => {
    if (!entityType || !entityId) return;

    const trimmed = content.trim();
    if (!trimmed) return;

    setSending(true);
    setError(null);

    try {
      const ensuredThread = thread ?? await ensureThread();
      if (!ensuredThread) return;

      setThread(ensuredThread);

      const response = await api.post<{ thread: DiscussionThreadRecord; message: DiscussionMessageRecord }>(
        `/chat-threads/${ensuredThread.id}/messages`,
        {
          content: trimmed,
          contentType: 'text',
        },
      );

      setThread(response.thread);
      setMessages((prev) => [...prev, response.message]);

      try {
        await api.post(`/chat-threads/${ensuredThread.id}/read`, {});
      } catch {
        // Ignore read receipt failures.
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send message');
      throw error;
    } finally {
      setSending(false);
    }
  }, [entityId, entityType, ensureThread, thread]);

  useEffect(() => {
    if (!canLoad) return;
    void refresh();
  }, [canLoad, refresh]);

  const appendMessage = useCallback((message: DiscussionMessageRecord) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev; // dedupe
      return [...prev, message];
    });
  }, []);

  return {
    thread,
    messages,
    loading,
    sending,
    error,
    refresh,
    sendMessage,
    appendMessage,
  };
}